import { Text, View } from "react-native";
import ScreenContainer from "../../components/ScreenContainer";
import { RootStackScreenProps } from "../types";
import { useIntercom } from "../../hooks";
import config from "../../config";
import { ActivityIndicator } from "react-native-paper";
import { gql, useMutation, useQuery } from "@apollo/client";
import { getEmploymentsQuery } from "./types";
import { GET_EMPLOYMENTS_QUERY } from "./query";
import { useEffect, useState } from "react";
import { ActionsContainer, Button } from "../../components";
import useSnackBar from "../../hooks/useSnackBar";
import SelectableEmploymentTile from "../../components/SelectableEmploymentTile";

export const changeEmploymentTypeTitle = "Employment Type";

interface UpdatePrimaryEmploymentMutationResponse {
  updatePrimaryEmployment: {
    success: boolean;
    message: string;
  };
}

interface UpdatePrimaryEmploymentMutationVariables {
  employmentId: string;
}

export const UPDATE_PRIMARY_EMPLOYMENT_MUTATION = gql`
  mutation updatePrimaryEmployment($employmentId: String!) {
    updatePrimaryEmployment(employmentId: $employmentId) {
      success
      message
    }
  }
`;

const ChangeEmploymentTypeScreen = ({ navigation }: RootStackScreenProps<"ChangeEmploymentType">) => {
  const [errorMessage, setErrorMessage] = useState("");
  const { data: employmentsData, loading: loadingEmployments } = useQuery<getEmploymentsQuery>(GET_EMPLOYMENTS_QUERY);

  const [updatePrimaryEmployment, { loading }] = useMutation<
    UpdatePrimaryEmploymentMutationResponse,
    UpdatePrimaryEmploymentMutationVariables
  >(UPDATE_PRIMARY_EMPLOYMENT_MUTATION);

  const showSnackBar = useSnackBar();

  const [selectedEmployment, setSelectedEmployment] = useState("");
  const [buttonDisabled, setButtonDisabled] = useState(true);
  const intercom = useIntercom();

  useEffect(() => {
    if (employmentsData?.currentPerson?.employments?.length ?? 0 > 0) {
      const primaryEmployment = employmentsData!.currentPerson.employments.find((e) => e.isPrimary);
      setSelectedEmployment(primaryEmployment?.id ?? "");
    }
  }, [employmentsData]);

  if (loadingEmployments) {
    return <ActivityIndicator className="flex-1 items-center justify-center bg-white" />;
  }

  const handleSwitch = (employmentId: string) => {
    setButtonDisabled(false);
    setSelectedEmployment(employmentId);
  };

  const handleSubmit = async () => {
    try {
      const response = await updatePrimaryEmployment({ variables: { employmentId: selectedEmployment } });

      const { success } = response?.data?.updatePrimaryEmployment ?? {};

      if (!success) throw new Error();

      showSnackBar({ message: "Employment Updated", icon: "check-circle", duration: 1500 });
      navigation.navigate("Account");
    } catch (error) {
      setErrorMessage((error as Error).message || "Something went wrong");
    }
  };

  return (
    <ScreenContainer navigationTitle={changeEmploymentTypeTitle} goBack={() => navigation.goBack()}>
      <View className="flex flex-1 justify-between">
        <View className="p-6">
          <Text className="text-regular-tight font-700 text-ink-darkest">Select Primary Employment</Text>
          <Text className="text-small-normal text-ink">
            You can have only one primary employment. Your primary employment determines your section affiliation and other
            membership details.{" "}
            <Text
              onPress={() => {
                intercom.present(config.intercomContent.cantFindFacility);
              }}
              className="text-link-small text-primary underline"
            >
              Learn More
            </Text>
          </Text>
          <View className="mt-8">
            {[...(employmentsData?.currentPerson?.employments ?? [])]
              .sort((a, b) => (a.isPrimary === b.isPrimary ? 0 : a.isPrimary ? -1 : 1))
              .map((employment) => (
                <SelectableEmploymentTile
                  key={employment.id}
                  employment={employment}
                  selectedEmployment={selectedEmployment}
                  handleSwitch={handleSwitch}
                />
              ))}
          </View>
        </View>
        <View>
          <ActionsContainer errorAlert errorMessage={errorMessage} onCloseErrorMessage={setErrorMessage}>
            <Button
              loading={loading}
              disabled={buttonDisabled || loading}
              testID="save-employment-button"
              onPress={handleSubmit}
              mode="contained"
            >
              Assign & Continue
            </Button>
          </ActionsContainer>
        </View>
      </View>
    </ScreenContainer>
  );
};

export default ChangeEmploymentTypeScreen;
